import { useTranslation } from 'react-i18next';

import { Autocomplete, Chip, TextField } from '@mui/material';

import { useCategoryTree } from '../../hooks';
import { flattenCategoryTree } from '../../lib/categoryTree';
import type { FlatCategoryNode } from '../../lib/categoryTree';

export interface PublicationRuleCategoryCriteriaProps {
  value: string[];
  onChange: (categoryIds: string[]) => void;
  disabled?: boolean;
}

/** MASTER_CATEGORY rule criteria — a multi-select over the tenant's full category tree (reuses `GET /categories/tree`, the same data source as the epic-catalog-005 category tree screen), flattened + indented by depth. */
export function PublicationRuleCategoryCriteria({
  value,
  onChange,
  disabled,
}: PublicationRuleCategoryCriteriaProps) {
  const { t } = useTranslation('catalog');
  const treeQuery = useCategoryTree();
  const flat = flattenCategoryTree(treeQuery.data ?? []);
  const selected = flat.filter((n) => value.includes(n.id));

  return (
    <Autocomplete
      multiple
      loading={treeQuery.isLoading}
      disabled={disabled}
      options={flat}
      value={selected}
      groupBy={(option) => option.ancestorNames[0] ?? option.name}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(_e, next: FlatCategoryNode[]) => onChange(next.map((n) => n.id))}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} size="small" label={option.name} {...tagProps} />;
        })
      }
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps} style={{ paddingLeft: 16 + option.depth * 16 }}>
            {option.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('publicationRulesPanel.criteriaForm.categoryPickerLabel')}
          placeholder={
            selected.length === 0 ? t('publicationRulesPanel.criteriaForm.categoryPickerPlaceholder') : undefined
          }
        />
      )}
    />
  );
}
